import { v4 as uuidv4 } from 'uuid'

import { createNameFromCustomAudiences } from '../../../../Multiplatform/LineItemForms/formatters'
import { formatDateBE } from '../../../../../constants/dates'
import {
  formatEndDate,
  formatStartDate
} from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/formatters'
import {
  formatAudiencesToBE,
  formatLocationsToBE
} from '../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/formatters'
import { calculateBudgetFromImpressions } from '../../../../Multiplatform/UploadAdCreative/ImpressionsBudgetSection/helpers'
import { getMultiLevelSelectPayloadValues } from '../../../../../components/MultiLevelSelect/formatters'

import {
  AUDIENCES_OPTION,
  CUSTOM_AUDIENCES,
  CUSTOM_AUDIENCES_EXCLUSION
} from '../../../../ReusableFormFields/Audiences/AudiencesFields/fields'
import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../../Multiplatform/Partnerships/RightForms/fields'
import {
  ENGAGEMENT,
  LEAD_GENERATION,
  REACH,
  TRAFFIC,
  VIDEO_VIEWS,
  WEB_CONVERSIONS
} from '../../../../../constants/campaigns'
import { END_DATE, START_DATE } from '../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import {
  BID_PRICE,
  GENDER_FEMALE,
  GENDER_MALE,
  GENDER_UNLIMITED,
  PIXEL_EVENT,
  PIXEL_ID,
  PLACEMENT_TIKTOK
} from '../../../LineItemForms/LineItemTikTokCreate/LineItemTikTokCreateForm/fields'
import { OPTION_CUSTOM, OPTION_YES } from '../../../../../constants/forms'
import { IMPRESSIONS } from '../../../../Multiplatform/UploadAdCreative/fields'
import { SHOW_COUNTRIES, SHOW_OPTION, SHOW_REGIONS } from '../../../../ReusableFormFields/GeolocationsFields/fields'
import { BUDGET_DAILY, BUDGET_LIFETIME, BUDGET_OPTION } from '../../../../Multiplatform/fields'
import { CUSTOM_AGE_RANGES } from '../../../../ReusableFormFields/AgeRangesFields/fields'
import { LANGUAGES } from '../../../../ReusableFormFields/LanguagesFields/fields'
import { COMMENT_DISABLED, OPTIMIZATION_GOAL, SHARE_DISABLED, VIDEO_DOWNLOAD_DISABLED } from './fields'
import {
  FREQUENCY_DAYS,
  FREQUENCY_IMPRESSIONS,
  FREQUENCY_OPTION,
  FREQUENCY_OPTION_NO_MORE_THAN_3_TIMES_EVERY_7_DAYS,
  FREQUENCY_OPTION_NO_MORE_THAN_ONCE_A_DAY
} from '../../../../ReusableFormFields/FrequencyFields/fields'
import {
  CLICK,
  CONVERT,
  ENGAGED_VIEW_FIFTEEN,
  FOLLOWERS,
  PROFILE_VIEWS,
  TRAFFIC_LANDING_PAGE_VIEW
} from './optimizationGoalOptions'
import { INTERESTS } from '../../../../ReusableFormFields/InterestsFields/fields'
import { INTEREST_KEYWORD_IDS } from './Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/AdditionalInterestCategoriesMultiselect'
import { PURCHASE_INTENTION_KEYWORD_IDS } from './Steps/CustomAudiencesStep/TikTokUploadCreativeAudienceFields/TikTokAdvancedAudienceTargetingSection/PurchaseIntentionCategoriesMultiselect'

const formatGenderToBE = values => {
  if (values[GENDER_MALE] && !values[GENDER_FEMALE]) {
    return GENDER_MALE
  } else if (values[GENDER_FEMALE] && !values[GENDER_MALE]) {
    return GENDER_FEMALE
  } else {
    return GENDER_UNLIMITED
  }
}

export const formatFrequencyToBE = ({ frequencyOption, frequencyImpression, frequencyDays }) => {
  switch (frequencyOption) {
    case FREQUENCY_OPTION_NO_MORE_THAN_3_TIMES_EVERY_7_DAYS:
      return {
        frequency: 3,
        frequency_schedule: 7
      }
    case FREQUENCY_OPTION_NO_MORE_THAN_ONCE_A_DAY:
      return {
        frequency: 1,
        frequency_schedule: 1
      }

    case OPTION_CUSTOM:
    default:
      return {
        frequency: Number(frequencyImpression),
        frequency_schedule: Number(frequencyDays)
      }
  }
}

export const getPromotionType = (campaignObjective, lineItemObjective) => {
  switch (campaignObjective.toLowerCase()) {
    case VIDEO_VIEWS:
      if (lineItemObjective === ENGAGED_VIEW_FIFTEEN) {
        return 'WEBSITE_OR_DISPLAY'
      } else {
        return 'WEBSITE'
      }
    case LEAD_GENERATION:
      return 'LEAD_GENERATION'
    case TRAFFIC:
    case ENGAGEMENT:
    case WEB_CONVERSIONS:
    default:
      return 'WEBSITE'
  }
}

export const getBillingEvent = (campaignObjective, lineItemObjective) => {
  switch (campaignObjective.toLowerCase()) {
    case REACH:
      return 'CPM'
    case TRAFFIC:
      if (lineItemObjective === CLICK) {
        return 'CPC'
      } else if (lineItemObjective === TRAFFIC_LANDING_PAGE_VIEW) {
        return 'OCPM'
      }
      break
    case VIDEO_VIEWS:
      return 'CPV'
    case ENGAGEMENT:
      if (lineItemObjective === FOLLOWERS) {
        return 'OCPM'
      } else if (lineItemObjective === PROFILE_VIEWS) {
        return 'CPC'
      }
      break
    case LEAD_GENERATION:
      if (lineItemObjective === CONVERT) {
        return 'OCPM'
      } else if (lineItemObjective === CLICK) {
        return 'CPC'
      }
      break
    case WEB_CONVERSIONS:
      if (lineItemObjective === CONVERT) {
        return 'OCPM'
      } else if (lineItemObjective === CLICK) {
        return 'CPC'
      }
      break
    default:
      return 'CPC'
  }
}

const getBidFieldName = billingEvent => {
  // Target Cost per action/bid cap value should map to conversion_bid for OCPM billing event,
  // or to bid for CPC/CPV/CPM billing events
  switch (billingEvent) {
    case 'OCPM':
      return 'conversion_bid'
    case 'CPC':
    case 'CPV':
    case 'CPM':
    default:
      return 'bid'
  }
}

export const transformLineItemValuesToBE = ({
  values,
  adAccountId,
  right,
  campaign,
  tikTokRegions,
  shouldPopulateExistingValues,
  pixelIdFromExistingLineItem,
  externalActionFromExistingLineItem,
  interests
}) => {
  const { allocation_type: rightAllocationType, bid_cap: rightBidCap } = right

  const {
    date_start: campaignDateStart,
    objective: campaignObjective = '',
    budget_optimize_on: isCampaignLevelBudgetSet
  } = campaign

  const isRightAllocationImpressions = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
  const isRightAllocationBudget = rightAllocationType === DIGITAL_ALLOCATION_TYPE_BUDGET

  const calculatedBudgetFromImpressions = calculateBudgetFromImpressions({
    impressions: values[IMPRESSIONS],
    rightBidCap
  })

  const isCampaignObjectiveReach = campaignObjective.toLowerCase() === REACH

  const includeBudgetInPayload = isRightAllocationImpressions || (isRightAllocationBudget && !isCampaignLevelBudgetSet)

  const shouldIncludeBidPrice =
    values[BID_PRICE] ||
    // if the right allocation is impressions, then we always include bid which is set on the right level
    (isRightAllocationImpressions && isCampaignObjectiveReach)

  const shouldIncludePixel =
    (campaignObjective.toLowerCase() === LEAD_GENERATION && values[OPTIMIZATION_GOAL] === CONVERT) ||
    (campaignObjective.toLowerCase() === WEB_CONVERSIONS && values[OPTIMIZATION_GOAL] === CONVERT)

  const formattedInterests = getMultiLevelSelectPayloadValues(values[INTERESTS], interests)

  const billingEvent = getBillingEvent(campaignObjective, values[OPTIMIZATION_GOAL])
  const bidFieldName = getBidFieldName(billingEvent)

  return {
    account: adAccountId,
    name: `${createNameFromCustomAudiences(values[CUSTOM_AUDIENCES])} - ${uuidv4().slice(0, 7)}`,
    campaign_id: campaign.id,
    campaign_start_date: formatDateBE(campaignDateStart),
    [OPTIMIZATION_GOAL]: values[OPTIMIZATION_GOAL].toUpperCase(),
    schedule_start_time: formatStartDate(values[START_DATE]),
    schedule_end_time: formatEndDate(values[END_DATE]),
    location: formatLocationsToBE(values[SHOW_OPTION], values[SHOW_COUNTRIES], values[SHOW_REGIONS], tikTokRegions),
    gender: formatGenderToBE(values),
    age: Object.keys(values[CUSTOM_AGE_RANGES]).filter(item => values[CUSTOM_AGE_RANGES][item]),
    ...(includeBudgetInPayload
      ? {
          ...(values[BUDGET_OPTION] === BUDGET_LIFETIME
            ? {
                buying_type: 'BUDGET_MODE_TOTAL',
                budget: isRightAllocationBudget ? values[BUDGET_LIFETIME] : calculatedBudgetFromImpressions
              }
            : {
                buying_type: 'BUDGET_MODE_DAY',
                budget: isRightAllocationBudget ? values[BUDGET_DAILY] : calculatedBudgetFromImpressions
              })
        }
      : {
          buying_type: 'BUDGET_MODE_INFINITE'
        }),

    ...(shouldIncludeBidPrice && {
      // if the right allocation is impressions, then we always use bid which is set on the right level
      [bidFieldName]: isRightAllocationImpressions && isCampaignObjectiveReach ? rightBidCap : values[BID_PRICE]
    }),
    bid_type: shouldIncludeBidPrice ? 'BID_TYPE_CUSTOM' : 'BID_TYPE_NO_BID',

    schedule_type: 'SCHEDULE_START_END',
    ...(isCampaignObjectiveReach &&
      formatFrequencyToBE({
        frequencyOption: values[FREQUENCY_OPTION],
        frequencyImpression: values[FREQUENCY_IMPRESSIONS],
        frequencyDays: values[FREQUENCY_DAYS]
      })),
    ...(values[AUDIENCES_OPTION] === OPTION_YES && {
      audience: formatAudiencesToBE(values[CUSTOM_AUDIENCES])
    }),
    ...(!!values[CUSTOM_AUDIENCES_EXCLUSION].length && {
      excluded_audience: formatAudiencesToBE(values[CUSTOM_AUDIENCES_EXCLUSION])
    }),
    // pixel
    ...(shouldIncludePixel && {
      pixel_id: shouldPopulateExistingValues ? pixelIdFromExistingLineItem : values[PIXEL_ID],
      external_action: shouldPopulateExistingValues ? externalActionFromExistingLineItem : values[PIXEL_EVENT]
    }),

    languages: values[LANGUAGES].map(({ value }) => value),
    placement: [PLACEMENT_TIKTOK],
    comment_disabled: values[COMMENT_DISABLED],
    share_disabled: values[SHARE_DISABLED],
    video_download_disabled: values[VIDEO_DOWNLOAD_DISABLED],
    billing_event: billingEvent,
    ...(campaignObjective.toLowerCase() === VIDEO_VIEWS && { bid_display_mode: 'CPV' }),
    promotion_type: getPromotionType(campaignObjective, values[OPTIMIZATION_GOAL]),
    ...(campaignObjective.toLowerCase() === LEAD_GENERATION && {
      promotion_target_type: 'EXTERNAL_WEBSITE'
    }),

    pacing: 'PACING_MODE_SMOOTH',
    creative_material_mode: 'CUSTOM',
    placement_type: 'PLACEMENT_TYPE_NORMAL',
    ...(formattedInterests.length && {
      interest_category: formattedInterests
    }),
    ...(values[INTEREST_KEYWORD_IDS].length && {
      [INTEREST_KEYWORD_IDS]: values[INTEREST_KEYWORD_IDS].map(item => item.id)
    }),
    ...(values[PURCHASE_INTENTION_KEYWORD_IDS].length && {
      [PURCHASE_INTENTION_KEYWORD_IDS]: values[PURCHASE_INTENTION_KEYWORD_IDS].map(item => item.id)
    })
  }
}
